import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatearFecha, type Pagina, type SujetoEvaluable, type SujetoEvaluableEntrada } from '@cmms/shared';
import { api, consulta } from '../servicios/api.js';
import { useSesion } from '../servicios/sesion.js';
import {
  Aviso,
  Cargando,
  Dialogo,
  DialogoConfirmar,
  ErroresFormulario,
  Etiqueta,
  Indicador,
  Vacio,
} from '../componentes/piezas.js';
import { IconoLupa } from '../componentes/iconos.js';
import { EnlaceRuta } from '../componentes/enlace-ruta.js';
import { SelectorLista, SelectorUbicacion, useCatalogo, useUbicaciones } from '../componentes/selectores.js';


/** Conteo de personas con un filtro de estado (o todas si no hay filtro). */
async function contar(activo?: 'true' | 'false'): Promise<number> {
  const respuesta = await api.get<Pagina<SujetoEvaluable>>(`/sujetos-evaluables${consulta({ pagina: 1, tamano: 25, activo })}`);
  return respuesta.total;
}

/**
 * Personal (§12.7): las personas que ejecutan o reciben OT. Viven aparte de los
 * activos: esta lista no mezcla equipos con personas.
 */
export function Personal(): JSX.Element {
  const { puede } = useSesion();
  const escribe = puede('sujeto_evaluable:escribir');
  const [buscar, setBuscar] = useState('');
  const [pagina, setPagina] = useState(1);
  const [estadoActivo, setEstadoActivo] = useState<'' | 'true' | 'false'>('');
  const [rolId, setRolId] = useState('');
  const [ubicacionId, setUbicacionId] = useState('');
  const [editando, setEditando] = useState<SujetoEvaluable | null | undefined>(undefined);
  const [eliminando, setEliminando] = useState<SujetoEvaluable | null>(null);

  const { data: roles } = useCatalogo('ROL_SUJETO_EVALUABLE');
  const { data: ubicaciones } = useUbicaciones();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['sujetos-evaluables', pagina, buscar, estadoActivo, rolId, ubicacionId],
    queryFn: () =>
      api.get<Pagina<SujetoEvaluable>>(
        `/sujetos-evaluables${consulta({ pagina, tamano: 25, buscar, activo: estadoActivo, rolId, ubicacionId })}`,
      ),
  });

  const { data: totales } = useQuery({
    queryKey: ['sujetos-evaluables', 'totales'],
    queryFn: async () => ({ total: await contar(), activas: await contar('true'), inactivas: await contar('false') }),
  });

  return (
    <>
      <div className="cabecera-pagina cabecera-lista">
        <div className="cabecera-lista-titulo">
          <h1>Personal</h1>
          <p className="cabecera-subtitulo">Personas que ejecutan o reciben órdenes de trabajo. No son activos.</p>
        </div>
        <div className="campo cabecera-lista-buscar">
          <IconoLupa />
          <input
            aria-label="Buscar personal"
            placeholder="Buscar persona"
            value={buscar}
            onChange={(e) => {
              setBuscar(e.target.value);
              setPagina(1);
            }}
          />
        </div>
        {escribe ? (
          <button type="button" className="boton" onClick={() => setEditando(null)}>
            + Nueva persona
          </button>
        ) : null}
      </div>

      <div className="rejilla-indicadores">
        <Indicador titulo="PERSONAS" valor={totales?.total ?? '…'} />
        <Indicador titulo="ACTIVAS" valor={totales?.activas ?? '…'} tono="verde" />
        <Indicador titulo="INACTIVAS" valor={totales?.inactivas ?? '…'} tono="gris" />
      </div>

      <div className="panel" style={{ marginBottom: 16 }}>
        <div className="panel-cuerpo filtros" style={{ gap: 12, marginBottom: 0 }}>
          <div style={{ minWidth: 160 }}>
            <SelectorLista
              id="personal-estado"
              etiqueta="Estado"
              valor={estadoActivo}
              alCambiar={(v) => {
                setEstadoActivo(v as '' | 'true' | 'false');
                setPagina(1);
              }}
              opciones={[
                { id: 'true', nombre: 'Activas' },
                { id: 'false', nombre: 'Inactivas' },
              ]}
              textoVacio="Todas"
            />
          </div>
          <div style={{ minWidth: 180 }}>
            <SelectorLista
              id="personal-rol"
              etiqueta="Rol"
              valor={rolId}
              alCambiar={(v) => {
                setRolId(v);
                setPagina(1);
              }}
              opciones={(roles ?? []).map((r) => ({ id: r.id, nombre: r.nombre }))}
              textoVacio="Todos los roles"
            />
          </div>
          <div style={{ minWidth: 200 }}>
            <SelectorLista
              id="personal-ubicacion"
              etiqueta="Ubicación"
              valor={ubicacionId}
              alCambiar={(v) => {
                setUbicacionId(v);
                setPagina(1);
              }}
              opciones={(ubicaciones ?? []).map((u) => ({ id: u.id, nombre: u.nombre }))}
              textoVacio="Todas las ubicaciones"
            />
          </div>
        </div>
      </div>

      {isError ? <Aviso tono="error">{error instanceof Error ? error.message : 'No se pudo cargar el personal.'}</Aviso> : null}

      <div className="panel">
        <div className="panel-cabecera tono-ot">
          <h2>Listado</h2>
          <span className="campo-nota">{data ? `${data.total} persona(s)` : ''}</span>
        </div>
        {isLoading ? (
          <div className="panel-cuerpo">
            <Cargando />
          </div>
        ) : (data?.datos ?? []).length === 0 ? (
          <div className="panel-cuerpo">
            <Vacio titulo="Sin personas" detalle="No hay personal con este filtro." />
          </div>
        ) : (
          <div className="tabla-contenedor">
            <table className="tabla">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Rol</th>
                  <th>Ubicación</th>
                  <th>Estado</th>
                  <th>Alta</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data!.datos.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <EnlaceRuta to={`/personal/${p.id}`}>
                        <strong>{p.nombreCompleto}</strong>
                      </EnlaceRuta>
                    </td>
                    <td>{p.rol}</td>
                    <td>{p.ubicacion ?? '—'}</td>
                    <td>
                      <Etiqueta tono={p.activo ? 'verde' : 'gris'}>{p.activo ? 'Activa' : 'Inactiva'}</Etiqueta>
                    </td>
                    <td>{formatearFecha(p.creadoEl)}</td>
                    <td>
                      {escribe ? (
                        <div className="acciones-fila">
                          <button type="button" className="boton secundario pequeno" onClick={() => setEditando(p)}>
                            Editar
                          </button>
                          <button type="button" className="boton peligro pequeno" onClick={() => setEliminando(p)}>
                            Eliminar
                          </button>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {data && data.total > data.tamano ? (
        <div className="acciones-cabecera" style={{ justifyContent: 'center', marginTop: 12 }}>
          <button type="button" className="boton secundario pequeno" disabled={pagina === 1} onClick={() => setPagina((p) => p - 1)}>
            Anterior
          </button>
          <span className="campo-nota">
            Página {pagina} de {Math.ceil(data.total / data.tamano)}
          </span>
          <button
            type="button"
            className="boton secundario pequeno"
            disabled={pagina * data.tamano >= data.total}
            onClick={() => setPagina((p) => p + 1)}
          >
            Siguiente
          </button>
        </div>
      ) : null}

      {editando !== undefined ? (
        <FormularioPersona persona={editando} alCerrar={() => setEditando(undefined)} />
      ) : null}

      {eliminando ? <EliminarPersona persona={eliminando} alCerrar={() => setEliminando(null)} /> : null}
    </>
  );
}

function FormularioPersona({ persona, alCerrar }: { persona: SujetoEvaluable | null; alCerrar: () => void }): JSX.Element {
  const cliente = useQueryClient();
  const { data: roles } = useCatalogo('ROL_SUJETO_EVALUABLE');
  const { data: ubicaciones } = useUbicaciones();

  const [nombre, setNombre] = useState(persona?.nombreCompleto ?? '');
  const [rolId, setRolId] = useState(persona?.rolId ?? '');
  const [ubicacionId, setUbicacionId] = useState(persona?.ubicacionId ?? '');
  const [activo, setActivo] = useState(persona?.activo ?? true);

  const puedeGuardar = nombre.trim() !== '' && rolId !== '';

  const guardar = useMutation({
    mutationFn: () => {
      const cuerpo: SujetoEvaluableEntrada = {
        nombreCompleto: nombre.trim(),
        rolId,
        ubicacionId: ubicacionId || null,
        activo,
      };
      return persona
        ? api.put<SujetoEvaluable>(`/sujetos-evaluables/${persona.id}`, cuerpo)
        : api.post<SujetoEvaluable>('/sujetos-evaluables', cuerpo);
    },
    onSuccess: async () => {
      await cliente.invalidateQueries({ queryKey: ['sujetos-evaluables'] });
      alCerrar();
    },
  });

  return (
    <Dialogo titulo={persona ? 'Editar persona' : 'Nueva persona'} alCerrar={alCerrar}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (puedeGuardar && !guardar.isPending) guardar.mutate();
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
      >
        <div className="campo">
          <label htmlFor="persona-nombre">Nombre completo</label>
          <input id="persona-nombre" value={nombre} onChange={(e) => setNombre(e.target.value)} autoFocus maxLength={200} />
        </div>

        <SelectorLista
          id="persona-rol"
          etiqueta="Rol"
          valor={rolId}
          alCambiar={setRolId}
          opciones={(roles ?? []).map((r) => ({ id: r.id, nombre: r.nombre }))}
          textoVacio="Elige un rol"
          requerido
        />

        <SelectorUbicacion id="persona-ubicacion" etiqueta="Ubicación" valor={ubicacionId} alCambiar={setUbicacionId} ubicaciones={ubicaciones} />

        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input type="checkbox" checked={activo} onChange={(e) => setActivo(e.target.checked)} />
          Activa
        </label>

        {guardar.isError ? <ErroresFormulario error={guardar.error} /> : null}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" className="boton secundario" onClick={alCerrar}>
            Cancelar
          </button>
          <button type="submit" className="boton" disabled={!puedeGuardar || guardar.isPending}>
            Guardar
          </button>
        </div>
      </form>
    </Dialogo>
  );
}

function EliminarPersona({ persona, alCerrar }: { persona: SujetoEvaluable; alCerrar: () => void }): JSX.Element {
  const cliente = useQueryClient();
  const eliminar = useMutation({
    mutationFn: () => api.delete(`/sujetos-evaluables/${persona.id}`),
    onSuccess: async () => {
      await cliente.invalidateQueries({ queryKey: ['sujetos-evaluables'] });
      alCerrar();
    },
  });

  return (
    <DialogoConfirmar
      titulo="Eliminar persona"
      mensaje={`«${persona.nombreCompleto}» dejará de aparecer en la lista. Sus órdenes de trabajo conservan su historial.`}
      etiqueta="Eliminar"
      cargando={eliminar.isPending}
      error={eliminar.error}
      alCerrar={alCerrar}
      alConfirmar={() => eliminar.mutate()}
    />
  );
}
